import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CATEGORIES from '../constants/categories';
import LOCATIONS from '../constants/locations';
import PhotoUploader from '../components/PhotoUploader';
import QuestionPicker from '../components/QuestionPicker';
import PageHeader from '../components/ui/PageHeader';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

export default function EditListing() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/listings/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        const l = res.data.listing;
        if (l.provider_id !== user?.id) {
          navigate(`/listings/${id}`);
          return;
        }
        setForm({
          title: l.title || '',
          description: l.description || '',
          category: l.category || '',
          price: l.price != null ? String(l.price) : '',
          location: l.location || '',
          cover_photo_url: l.cover_photo_url || '',
          photos: l.photos || [],
        });
        setQuestions(
          (l.listing_questions || []).map(lq => ({
            question_id: lq.question.id,
            required: lq.required,
          }))
        );
      })
      .catch(() => navigate('/company/listings'))
      .finally(() => setFetching(false));
  }, [id, authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/listings/${id}`,
        { ...form, price: form.price ? parseFloat(form.price) : null, questions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/company/listings');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update listing');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="bg-sand-50 min-h-screen flex items-center justify-center">
        <div
          role="status"
          aria-label="Loading"
          className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin"
        />
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="bg-sand-50 min-h-screen pb-24 sm:pb-0">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PageHeader title="Edit Listing" subtitle="Update your service details" />

        <form id="edit-listing-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          <Input
            id="title"
            label="Title"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Professional deep cleaning"
            required
          />

          <div>
            <label className="text-sm font-medium text-ink-700 block mb-1">Category</label>
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              required
              className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
            >
              <option value="">Select a category…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <Input
            id="description"
            label="Description"
            as="textarea"
            rows={4}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Describe your service in detail…"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              id="price"
              label="Price (AED)"
              type="number"
              min="0"
              value={form.price}
              onChange={e => setForm({ ...form, price: e.target.value })}
              placeholder="0"
            />
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Location</label>
              <select
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
              >
                <option value="">Select…</option>
                {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Cover photo */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">Cover Photo</label>
            {form.cover_photo_url
              ? <div className="relative">
                  <img src={form.cover_photo_url} alt="cover" className="w-full h-48 object-cover rounded-md" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_photo_url: '' })}
                    className="absolute top-2 right-2 bg-white text-ink-700 text-xs px-2 py-1 rounded shadow-card"
                  >
                    Remove
                  </button>
                </div>
              : <PhotoUploader
                  label="Upload Cover"
                  onUpload={url => setForm({ ...form, cover_photo_url: url })}
                />
            }
          </div>

          {/* Additional photos */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Additional Photos ({form.photos.length}/5)
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {form.photos.map((url, i) => (
                <div key={i} className="relative">
                  <img src={url} alt="" className="w-full h-24 object-cover rounded-md" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                    className="absolute top-1 right-1 bg-white text-ink-700 text-xs px-1.5 py-0.5 rounded shadow-card"
                  >
                    ×
                  </button>
                </div>
              ))}
              {form.photos.length < 5 &&
                <PhotoUploader
                  label="+ Add photo"
                  onUpload={url => setForm({ ...form, photos: [...form.photos, url] })}
                />
              }
            </div>
          </div>

          {/* Questions asked at order time */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Questions to ask customers (optional)
            </label>
            <p className="text-xs text-ink-400 mb-3">
              Pick from the question bank. Customers will fill these in when they place an order.
            </p>
            <QuestionPicker value={questions} onChange={setQuestions} />
          </div>

          {error && <p className="text-sm text-uae-red">{error}</p>}

          <div className="hidden sm:flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 justify-center"
              onClick={() => navigate('/company/listings')}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={loading} className="flex-1 justify-center">
              Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Mobile fixed submit */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-sand-200 p-4 sm:hidden z-30">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            className="flex-1 justify-center"
            onClick={() => navigate('/company/listings')}
          >
            Cancel
          </Button>
          <Button type="submit" form="edit-listing-form" variant="primary" loading={loading} className="flex-1 justify-center">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
